import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

/**
 * A utility hook that executes a callback function after a valid user gesture on the web.
 * @param callback The function to execute.
 * @param shouldWait If true, waits for interaction. If false, executes immediately.
 * @param continues If true, the listener stays alive and fires the callback on every interaction.
 */
export function useUserInteraction(callback: () => void, shouldWait: boolean = true, continues: boolean = false) {
  const hasInteracted = useRef(false);

  useEffect(() => {
    // If not web, or we don't need to wait, execute immediately and exit.
    if (Platform.OS !== 'web' || !shouldWait) {
      if (shouldWait) callback();
      return;
    }

    // If it's a one-time listener and we already interacted, execute and skip binding.
    if (!continues && hasInteracted.current) {
      callback();
      return;
    }

    // 👇 THE FIX: Swapped 'touchstart' for 'touchend' and 'mousedown'.
    // The browser strictly requires the tap/click to FINISH before granting the audio token.
    const interactionEvents = ['click', 'keydown', 'touchend', 'mousedown'] as const;

    const handleInteraction = () => {
      hasInteracted.current = true;

      // Only remove manually if it is NOT supposed to continue
      if (!continues) {
        interactionEvents.forEach((event) => {
          document.removeEventListener(event, handleInteraction, { capture: true });
        });
      }

      // Execute the requested action (your unlockSafariAudio function)
      callback();
    };

    // Attach listeners to wait for the user gesture
    interactionEvents.forEach((event) => {
      document.addEventListener(event, handleInteraction, {
        once: !continues,
        passive: true,
        // Intercept the event on its way DOWN before modals stop it
        capture: true,
      });
    });

    return () => {
      interactionEvents.forEach((event) => {
        document.removeEventListener(event, handleInteraction, { capture: true });
      });
    };
  }, [callback, shouldWait, continues]);
}
